import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Sparkles, Heart, ChevronDown, ChevronRight } from "lucide-react";
import { api, FILE_URL, formatBRL, displayBrand } from "../lib/api";
import { useMaxInstallments } from "../lib/installments";
import { usePixDiscount } from "../lib/pixDiscount";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { diversifyProducts } from "../lib/diversify";
import { fetchRecommendations, bumpInteraction } from "../lib/recommendations";
import ProductCard from "../components/ProductCard";
import LoginPromptModal from "../components/LoginPromptModal";
import TrustBadges from "../components/TrustBadges";

const FALLBACK = "https://images.unsplash.com/photo-1611652022419-a9419f74343d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function ProductPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const touchStartX = useRef(null);
  const [related, setRelated] = useState([]);
  const [complements, setComplements] = useState([]);
  const [openAcc, setOpenAcc] = useState("specs");
  const { toggle, has } = useWishlist();
  const { add: addToCart } = useCart();
  const maxInstallments = useMaxInstallments();
  const pixPct = usePixDiscount();
  const { user } = useAuth();
  const [cartLoading, setCartLoading] = useState(false);
  const [cartError, setCartError] = useState("");
  const [promptOpen, setPromptOpen] = useState(false);
  // Fresh seed on each visit AND for each product → "Você também vai gostar"
  // surfaces a different mix every time. We mix the product id into the seed so
  // two products in the same session don't share an order.
  const relatedSeed = useMemo(
    () => Date.now() ^ Math.floor(Math.random() * 1e9) ^ [...id].reduce((a, c) => ((a * 31) ^ c.charCodeAt(0)) >>> 0, 0),
    [id]
  );

  const handleTryOn = (e) => {
    e.preventDefault();
    if (!user) {
      setPromptOpen(true);
      return;
    }
    nav(`/try-on/experimentar?produto=${product.id}`);
  };

  const handleAddToCart = async () => {
    setCartError("");
    setCartLoading(true);
    try {
      await addToCart(product.id);
    } catch (e) {
      setCartError(e?.response?.data?.detail || "Não foi possível adicionar ao carrinho.");
    } finally {
      setCartLoading(false);
    }
  };

  const handleBuyNow = async () => {
    setCartError("");
    setCartLoading(true);
    try {
      await addToCart(product.id);
      nav("/checkout");
    } catch (e) {
      setCartError(e?.response?.data?.detail || "Não foi possível iniciar a compra.");
      setCartLoading(false);
    }
  };

  useEffect(() => {
    setActiveImg(0);
    api.get(`/products/${id}`).then((r) => {
      const src = r.data;
      setProduct(src);
      // Track view + bump the per-session interaction counter so the next
      // affinity request uses a lower ε (more personalised mix).
      bumpInteraction();
      api.post("/metrics/event", { event_type: "product_view", product_id: src.id }).catch(() => {});
      // Auto-suggested "Ficou Lindo Assim" / "Complete o look" — backend
      // returns up to 20 stylistically/price-relevant candidates sorted by
      // relevance; we then diversify and pick 4, reshuffled per visit via
      // `relatedSeed`, so revisiting the same PDP surfaces a varied mix.
      api.get(`/products/${src.id}/similar`, { params: { limit: 20 } })
        .then((rr) => {
          const pool = rr.data || [];
          const diversified = diversifyProducts(pool, { limit: 4, seed: relatedSeed });
          setComplements(diversified);
        })
        .catch(() => setComplements([]));
      // "Você também vai gostar": M218 — try affinity first (server-curated,
      // blends affinity + diversified per ε-greedy when flag is on and the
      // user has signals). Fall back to the local style/combines/price-band
      // scoring + diversification when the backend returns "diversified".
      fetchRecommendations({
        context: "pdp_related",
        productId: src.id,
        limit: 4,
        localPool: async () => {
          const rr = await api.get("/products", { params: { limit: 200 } });
          const pool = (rr.data || []).filter((p) => p.id !== src.id);
          const srcStyles = new Set(src.style || []);
          const srcCombines = new Set(src.combines_with || []);
          const srcPrice = Number(src.price) || 0;
          const scored = pool.map((p) => {
            const styleOverlap = (p.style || []).reduce((n, s) => n + (srcStyles.has(s) ? 1 : 0), 0);
            const combinesOverlap = (p.combines_with || []).reduce((n, c) => n + (srcCombines.has(c) ? 1 : 0), 0);
            const priceProx = srcPrice > 0
              ? Math.max(0, 1 - Math.abs((Number(p.price) || 0) - srcPrice) / srcPrice)
              : 0;
            return { p, score: styleOverlap * 2 + combinesOverlap * 1.5 + priceProx };
          });
          scored.sort((a, b) => b.score - a.score);
          return scored.slice(0, 30).map((x) => x.p);
        },
        diversifyOpts: { seed: relatedSeed },
      })
        .then(({ items }) => setRelated(items))
        .catch(() => {});
    }).catch(() => nav("/"));
  }, [id, nav, relatedSeed]);

  if (!product) {
    return <div className="p-20 text-center text-[#A8A8A8]">Carregando...</div>;
  }

  const images = product.images?.length ? product.images.map((p) => FILE_URL(p)) : [FALLBACK];
  const isFav = has(product.id);
  const brandLabel = displayBrand(product);
  const hasConjunto = complements.length > 0;

  return (
    <div className="bg-white" data-testid="product-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
        <nav className="text-xs text-[#A8A8A8] flex items-center gap-2">
          <Link to="/">Início</Link>
          <ChevronRight size={12} />
          <Link to={`/joias/${product.category}`}>{product.category}</Link>
          <ChevronRight size={12} />
          <span className="text-[#6B6B6B]">{product.name}</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-[80px_1fr_1fr] gap-6 md:gap-10">
        {/* Thumbnails */}
        <div className="hidden md:flex flex-col gap-3">
          {images.map((src, i) => (
            <button
              key={i}
              data-testid={`thumb-${i}`}
              onClick={() => setActiveImg(i)}
              className={`aspect-square overflow-hidden ${activeImg === i ? "ring-1 ring-[#B8A88A]" : "opacity-60"}`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
        {/* Main image — swipeable on mobile, dots below act as position indicator + tap-to-jump */}
        <div>
          <div
            className="aspect-[4/5] bg-[#FAF9F7] overflow-hidden touch-pan-y select-none"
            onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              if (touchStartX.current == null || images.length <= 1) return;
              const dx = e.changedTouches[0].clientX - touchStartX.current;
              if (Math.abs(dx) > 40) {
                setActiveImg((i) => {
                  if (dx < 0) return (i + 1) % images.length;      // swipe left → next
                  return (i - 1 + images.length) % images.length;  // swipe right → prev
                });
              }
              touchStartX.current = null;
            }}
          >
            <img src={images[activeImg]} alt={product.name} className="w-full h-full object-cover pointer-events-none" data-testid="product-main-image" draggable={false} />
          </div>
          {/* Mobile-only dots — one per photo, active = filled #B8A88A. Desktop keeps the side thumbnails above. */}
          {images.length > 1 && (
            <div className="md:hidden flex items-center justify-center gap-2 pt-3" data-testid="pdp-mobile-dots">
              {images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Foto ${i + 1}`}
                  data-testid={`pdp-dot-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    activeImg === i
                      ? "bg-[#B8A88A] scale-110"
                      : "bg-transparent border border-[#B8A88A]/50"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
        {/* Info */}
        <div className="md:sticky md:top-24 md:self-start">
          {(product.is_unique || product.status === "unique") && (
            <span className="inline-block bg-[#F5EFE8] text-[#1A1A1A] text-[10px] uppercase tracking-wider px-2.5 py-1 mb-4">
              Peça Única
            </span>
          )}
          {brandLabel && <p className="overline text-[#A8A8A8] mb-3">{brandLabel}</p>}
          <h1 className="font-display text-4xl md:text-5xl mb-4 leading-tight">{product.name}</h1>
          <p className="text-2xl font-light mb-1">{formatBRL(product.price)}</p>
          {/* Single discrete line combining PIX and installments perks.
              Rendered only when at least one perk is active. Segments joined
              by " · " for a minimalist, restaurant-menu feel. */}
          {(() => {
            const pieces = [];
            if (pixPct > 0 && product.price > 0) {
              pieces.push(`PIX com ${pixPct}% off`);
            }
            if (maxInstallments > 1 && product.price > 0) {
              pieces.push(`Até ${maxInstallments}x sem juros`);
            }
            if (pieces.length === 0) return <div className="mb-6" aria-hidden />;
            return (
              <p
                className="text-sm text-[#8C7B5E] mb-6"
                data-testid="pdp-payment-perks"
              >
                {pieces.join(" · ")}
              </p>
            );
          })()}
          {product.highlight_phrase && (
            <p className="font-italic-display text-lg text-[#8C7B5E] mb-6">"{product.highlight_phrase}"</p>
          )}
          <p className="text-[#6B6B6B] text-sm leading-relaxed mb-8">{product.short_description}</p>

          <div className="space-y-3 mb-8">
            <button
              data-testid="buy-now-btn"
              onClick={handleBuyNow}
              disabled={cartLoading}
              className="btn-primary w-full disabled:opacity-60"
            >
              {cartLoading ? "Reservando…" : "Comprar"}
            </button>
            <button
              data-testid="add-to-cart-btn"
              onClick={handleAddToCart}
              disabled={cartLoading}
              className="w-full inline-flex items-center justify-center border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white transition-colors py-3.5 px-4 disabled:opacity-60"
              style={{ letterSpacing: "0.15em", fontFamily: "'Jost', sans-serif", fontSize: "11px", textTransform: "uppercase" }}
            >
              + Adicionar ao carrinho
            </button>
            <Link to={`/try-on?produto=${product.id}`} onClick={handleTryOn} data-testid="tryon-pdp-btn" className="btn-accent w-full">
              <Sparkles size={14} className="mr-2" /> Experimentar em Você
            </Link>
            <button
              data-testid="wishlist-pdp-btn"
              onClick={() => toggle(product)}
              className="w-full inline-flex items-center justify-center text-[#6B6B6B] hover:text-[#1A1A1A] py-2"
              style={{ letterSpacing: "0.1em", fontFamily: "'Jost', sans-serif", fontSize: "12px", textTransform: "uppercase" }}
            >
              <Heart size={14} className="mr-2" fill={isFav ? "#B8A88A" : "none"} stroke={isFav ? "#B8A88A" : "currentColor"} />
              {isFav ? "Salvo na Wishlist" : "Wishlist"}
            </button>
            {cartError && <p className="text-sm text-[#B85050]" data-testid="cart-error">{cartError}</p>}
          </div>

          <div className="border-t border-[#E8E4DF]">
            <Accordion title="Especificações" id="specs" open={openAcc} setOpen={setOpenAcc}>
              <div className="space-y-1 text-sm text-[#6B6B6B]">
                {(() => {
                  const cat = product.category || "";
                  const catSize = {
                    "Anéis": product.size_anel,
                    "Colares": product.size_colares,
                    "Brincos": product.size_brincos,
                    "Pingentes": product.size_pingente,
                    "Pulseiras": product.size_pulseira,
                    "Outros": product.size_conjunto || product.size_diversos,
                  }[cat] || product.size;
                  return catSize ? <p><span className="text-[#1A1A1A]">Tamanho:</span> {catSize}</p> : null;
                })()}
                {product.purity && <p><span className="text-[#1A1A1A]">Peso das pedras:</span> {product.purity}</p>}
                {product.weight_g != null && <p><span className="text-[#1A1A1A]">Peso da peça:</span> {product.weight_g}g</p>}
                {product.collection && <p><span className="text-[#1A1A1A]">Coleção:</span> {product.collection}</p>}
              </div>
            </Accordion>
            <Accordion title="História da peça" id="story" open={openAcc} setOpen={setOpenAcc}>
              <p className="text-sm text-[#6B6B6B] leading-relaxed">{product.full_description || "—"}</p>
            </Accordion>
            <Accordion title="Entrega e garantia" id="delivery" open={openAcc} setOpen={setOpenAcc}>
              <p className="text-sm text-[#6B6B6B] leading-relaxed">
                Pagamento processado com segurança pelo Mercado Pago. Frete grátis para todo o Brasil. Certificado de Garantia e Nota Fiscal inclusos em toda compra.
              </p>
            </Accordion>
          </div>

          {/* Trust signals on PDP */}
          <div className="pt-8">
            <TrustBadges variant="row" />
          </div>
        </div>
      </div>

      {/* Conjunto OR Related (not both) */}
      {hasConjunto ? (
        <section className="bg-[#FAF9F7] mt-24 py-20" data-testid="complete-look">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl md:text-4xl mb-2">Ficou lindo assim.</h2>
              <p className="font-italic-display text-lg text-[#6B6B6B]">Que tal completar o conjunto?</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {complements.map((c) => <ProductCard key={c.id} product={c} />)}
            </div>
          </div>
        </section>
      ) : (
        related.length > 0 && (
          <section className="py-24" data-testid="related-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <h2 className="font-display text-3xl md:text-4xl text-center mb-12">Você também vai gostar</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {related.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            </div>
          </section>
        )
      )}
      <LoginPromptModal
        open={promptOpen}
        onClose={() => setPromptOpen(false)}
        redirectTo={product ? `/try-on/experimentar?produto=${product.id}` : "/try-on"}
      />
    </div>
  );
}

function Accordion({ title, id, open, setOpen, children }) {
  const isOpen = open === id;
  return (
    <div className="border-b border-[#E8E4DF]">
      <button
        onClick={() => setOpen(isOpen ? "" : id)}
        className="w-full py-4 flex items-center justify-between text-left overline"
        data-testid={`acc-${id}`}
      >
        {title}
        <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && <div className="pb-5">{children}</div>}
    </div>
  );
}
